import { useState, useEffect } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

export default function ProfilePage() {
  const { user, loading, logout, updateProfile, refreshUser } = useAuth()
  const [searchParams] = useSearchParams()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const verified = searchParams.get('verified') === 'true'

  useEffect(() => {
    if (verified) {
      refreshUser()
    }
  }, [verified, refreshUser])

  useEffect(() => {
    if (user?.name) {
      setName(user.name)
    }
  }, [user])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-archive-gold border-t-transparent" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage('')

    const result = await updateProfile({ name: name.trim() || undefined })

    if (result.success) {
      setMessage('Profile updated successfully')
    } else {
      setMessage(result.error || 'Failed to update profile')
    }

    setSaving(false)
  }

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="animate-fade-in py-16 sm:py-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-display text-4xl text-archive-cream">
            Your Profile
          </h1>
          <p className="mt-4 text-archive-muted">
            Manage your account settings
          </p>
        </div>

        {verified && (
          <div className="mb-8 p-4 bg-archive-gold/10 border border-archive-gold/30 rounded-lg text-center">
            <p className="text-archive-gold">
              Successfully signed in! Welcome to The Archive Network.
            </p>
          </div>
        )}

        {/* Profile Card */}
        <div className="artifact-frame p-8">
          {/* Avatar & Email */}
          <div className="flex items-center gap-4 mb-8 pb-8 border-b border-archive-stone/30">
            <div className="w-16 h-16 rounded-full bg-archive-gold/10 flex items-center justify-center text-archive-gold font-display text-2xl border border-archive-gold/30">
              {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
            </div>
            <div>
              <p className="text-archive-cream font-medium">
                {user.name || 'Archive Member'}
              </p>
              <p className="text-archive-muted text-sm">{user.email}</p>
            </div>
          </div>

          {/* Edit Profile Form */}
          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm text-archive-muted mb-2">
                Display Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 bg-archive-slate border border-archive-stone rounded-lg text-archive-cream placeholder-archive-muted/50 focus:outline-none focus:border-archive-gold transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm text-archive-muted mb-2">
                Email
              </label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full px-4 py-3 bg-archive-charcoal border border-archive-stone rounded-lg text-archive-muted cursor-not-allowed"
              />
              <p className="mt-1 text-xs text-archive-muted">
                Email cannot be changed
              </p>
            </div>

            {message && (
              <p className={`text-sm text-center ${message.includes('success') ? 'text-archive-gold' : 'text-red-400'}`}>
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 bg-gradient-to-r from-archive-gold to-archive-amber text-archive-black font-semibold rounded-lg hover:shadow-lg hover:shadow-archive-gold/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>

          {/* Account Info */}
          <div className="mt-8 pt-8 border-t border-archive-stone/30">
            <h3 className="font-display text-archive-cream mb-4">Account Info</h3>
            <div className="space-y-2 text-sm">
              <p className="text-archive-muted">
                Member since: {new Date(user.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* Logout */}
          <div className="mt-8 pt-8 border-t border-archive-stone/30">
            <button
              onClick={handleLogout}
              className="w-full py-3 border border-red-500/50 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
